import React, { useEffect, useRef, useState } from "react";
import { Minus, Plus, Type, AlignJustify, Play, Pause, Maximize2 } from "lucide-react";
import { transposeChordLine, transposeKey, shouldUseFlats } from "@/lib/transpose";

const SPEEDS = [0.25, 0.5, 1, 1.5, 2];

export default function ChordViewer({ song, stage = false, initialSteps = 0 }) {
  const [steps, setSteps] = useState(initialSteps);
  const [font, setFont] = useState(stage ? 22 : 16);
  const [spacing, setSpacing] = useState(1.6);
  const [scrolling, setScrolling] = useState(false);
  const [speed, setSpeed] = useState(0.5);
  const [twoCol, setTwoCol] = useState(false);
  const scrollRef = useRef(null);
  const rafRef = useRef(null);

  const baseKey = song.arrangement_key || song.original_key || "C";
  const currentKey = transposeKey(baseKey, steps);
  const flats = shouldUseFlats(currentKey);

  useEffect(() => {
    if (!scrolling) { cancelAnimationFrame(rafRef.current); return; }
    let last = performance.now();
    const step = (now) => {
      const el = scrollRef.current;
      if (el) { el.scrollTop += (now - last) / 1000 * speed * 40; }
      last = now;
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [scrolling, speed]);

  const sections = song.sections && song.sections.length ? song.sections : null;

  return (
    <div className={stage ? "stage-canvas rounded-none" : ""}>
      {/* control bar */}
      <div className={`flex flex-wrap items-center gap-2 p-3 rounded-xl mb-4 ${stage ? "bg-[#0A0F1D] border border-white/10" : "wf-card"}`}>
        <div className="flex items-center gap-1.5 bg-[#121A2C] px-2 py-1 rounded-lg border border-[#E5C378]/30">
          <button onClick={() => setSteps((s) => s - 1)} data-testid="btn-transpose-down" className="p-1.5 rounded-md hover:bg-white/10 text-[#E5C378]"><Minus className="w-4 h-4" /></button>
          <div className="text-center px-1">
            <div className="font-mono font-bold text-[#E5C378] text-base leading-none">{currentKey}</div>
            <div className="text-[9px] text-[#6E7D97] leading-none mt-0.5">key</div>
          </div>
          <button onClick={() => setSteps((s) => s + 1)} data-testid="btn-transpose-up" className="p-1.5 rounded-md hover:bg-white/10 text-[#E5C378]"><Plus className="w-4 h-4" /></button>
        </div>
        {steps !== 0 && <button onClick={() => setSteps(0)} className="text-xs text-[#8A97AC] hover:text-[#E5C378]">Reset ({baseKey})</button>}

        <div className="flex items-center gap-1 bg-white/5 px-2 py-1 rounded-lg">
          <Type className="w-3.5 h-3.5 text-[#8A97AC]" />
          <button onClick={() => setFont((f) => Math.max(12, f - 2))} className="px-1.5 text-[#B8C1D1]">−</button>
          <span className="text-xs text-[#B8C1D1] w-6 text-center">{font}</span>
          <button onClick={() => setFont((f) => Math.min(40, f + 2))} className="px-1.5 text-[#B8C1D1]">+</button>
        </div>
        <button onClick={() => setSpacing((s) => (s >= 2.4 ? 1.2 : s + 0.4))} className="flex items-center gap-1 bg-white/5 px-2 py-1.5 rounded-lg text-xs text-[#B8C1D1]"><AlignJustify className="w-3.5 h-3.5" /> Spacing</button>
        {!stage && <button onClick={() => setTwoCol((v) => !v)} className="hidden lg:block bg-white/5 px-2 py-1.5 rounded-lg text-xs text-[#B8C1D1]">{twoCol ? "1 column" : "2 columns"}</button>}

        <div className="flex items-center gap-1 ml-auto">
          <button onClick={() => setScrolling((v) => !v)} data-testid="btn-auto-scroll-toggle"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg wf-gold-btn font-semibold text-xs">
            {scrolling ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />} Auto-scroll
          </button>
          <select value={speed} onChange={(e) => setSpeed(Number(e.target.value))} className="bg-[#121A2C] border border-white/10 rounded-lg px-2 py-1.5 text-xs text-[#F7F5F0]">
            {SPEEDS.map((s) => <option key={s} value={s}>{s}x</option>)}
          </select>
        </div>
      </div>

      {/* chart body */}
      <div ref={scrollRef} data-testid="chord-chart-body"
        className={`overflow-auto ${stage ? "px-2 sm:px-6" : "wf-card rounded-2xl p-5"}`}
        style={{ maxHeight: stage ? "calc(100vh - 200px)" : "70vh" }}>
        {!sections ? (
          <div className="font-mono whitespace-pre-wrap" style={{ fontSize: font, lineHeight: spacing }}>
            {song.lyrics || "No chart available for this song yet."}
          </div>
        ) : (
          <div className={twoCol ? "columns-2 gap-10" : ""}>
            {sections.map((sec, i) => (
              <div key={i} className="mb-6 break-inside-avoid">
                <div className="font-mono uppercase tracking-widest text-sm font-bold mb-2" style={{ color: stage ? "#4E97FF" : "#7EA293" }}>{sec.name}</div>
                {sec.lines.map((ln, j) => (
                  <div key={j} style={{ marginBottom: `${spacing * 0.4}rem` }}>
                    {ln.chords != null && ln.chords.trim() !== "" && (
                      <pre className="font-mono font-bold m-0 chord-token" style={{ fontSize: font, color: stage ? "#FFD56B" : "#FFD56B", whiteSpace: "pre-wrap" }}>
                        {transposeChordLine(ln.chords, steps, flats)}
                      </pre>
                    )}
                    {ln.lyrics != null && ln.lyrics.trim() !== "" && (
                      <div className="font-sans" style={{ fontSize: font, color: stage ? "#FFFFFF" : "#E7EAF0", lineHeight: 1.2 }}>{ln.lyrics}</div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
